import React, { useState } from "react";
import { 
  FaFacebookF, 
  FaInstagram, 
  FaWhatsapp, 
  FaEnvelope, 
  FaPhone,
  FaStore,
  FaTruck,
  FaShieldAlt,
  FaUndo,
  FaTiktok,
  FaMobile,
  FaCrown,
  FaCheckCircle,
  FaRegPaperPlane,
  FaRegEnvelope,
  FaRegClock,
  FaRegBuilding,
  FaRegHeart,
  FaExclamationTriangle,
  FaBuilding
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { Button, Form, InputGroup } from "react-bootstrap";
import { Link } from "react-router-dom";
import "./Footer.css";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState("");
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeLink, setActiveLink] = useState(null);

  // Handle subscribe with proper error handling
  const handleSubscribe = async (e) => {
    e.preventDefault();
    
    // Clear previous messages
    setError(null);
    setSuccess(null);
    
    if (!email) {
      setError("Please enter your email address");
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await fetch("https://whats.asbfashion.com/api/subscribe.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({ email: email }),
      });
      
      const result = await response.json();
      
      console.log("Subscription response:", result); // For debugging
      
      if (result.status === "success") {
        // Success - email subscribed successfully
        setSuccess("Successfully subscribed! Check your inbox for updates.");
        setEmail("");
        setIsSubscribed(true);
        
        // Auto hide success message after 5 seconds
        setTimeout(() => {
          setSuccess(null);
          setIsSubscribed(false);
        }, 5000);
      } 
      else if (result.status === "exists" || result.message?.toLowerCase().includes("already")) {
        // Email already exists
        setError("This email is already subscribed to our newsletter!");
        
        // Auto hide error after 5 seconds
        setTimeout(() => {
          setError(null);
        }, 5000);
      }
      else if (result.status === "error") {
        // Other errors
        setError(result.message || "Something went wrong. Please try again.");
        
        // Auto hide error after 5 seconds
        setTimeout(() => {
          setError(null);
        }, 5000);
      }
      else {
        // Unknown response
        setError("Unable to subscribe. Please try again later.");
        
        setTimeout(() => {
          setError(null);
        }, 5000);
      }
    } catch (error) {
      console.error("Error subscribing:", error);
      setError("Network error. Please check your connection and try again.");
      
      setTimeout(() => {
        setError(null);
      }, 5000);
    } finally {
      setIsLoading(false);
    }
  };

  // Quick links data
  const quickLinks = [
    { name: "Home", path: "/" },
    { name: "Men's Collection", path: "/men" },
    { name: "Women's Collection", path: "/women" },
    { name: "Kids Collection", path: "/kids" },
    { name: "Lookbook", path: "/lookbook" },
    { name: "Store Locator", path: "/stores" }
  ];

  // Social media data
  const socialMedia = [
    { icon: <FaFacebookF />, url: "https://www.facebook.com/share/18fvBYoCn5/?mibextid=wwXIfr", name: "Facebook" },
    { icon: <FaInstagram />, url: "https://www.instagram.com/asbfashionsl?igsh=MXdicXBvcHdndGs0Mg==", name: "Instagram" },
    { icon: <FaTiktok />, url: "https://www.tiktok.com/@asbfashion3?_r=1&_t=ZS-969e9ly6lE4", name: "TikTok" },
    { icon: <FaWhatsapp />, url: "https://wa.me/94719057057", name: "WhatsApp" }
  ];

  const footerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.8, staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  return (
    <>
      <motion.footer 
        className="asb-footer-pro"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={footerVariants}
      >
        <div className="footer-bg-elements">
          <div className="bg-grid"></div>
          <div className="bg-glow glow-1"></div>
          <div className="bg-glow glow-2"></div>
        </div>

        <div className="footer-main">
          <div className="container">
            <div className="footer-grid">
              {/* Brand Column - ASB Fashion Text instead of Logo */}
              <motion.div className="footer-col brand-col" variants={itemVariants}>
                <div className="brand-container">
                  <div className="text-logo-container">
                    <div className="brand-text-logo">
                      <span className="brand-asb">ASB</span>
                      <span className="brand-fashion">FASHION</span>
                    </div>
                    <div className="brand-badge">
                      <FaCrown className="brand-crown" />
                      <span>Since 1989</span>
                    </div>
                  </div>
                  <p className="brand-description">
                    Sri Lanka's premier fashion destination with 17 branches islandwide, 
                    delivering exceptional style and quality since 1989.
                  </p>
                  
                  {/* Mother Company Info */}
                  <div className="mother-company">
                    <div className="mother-company-badge">
                      <FaBuilding className="mother-icon" />
                      <span>Part of</span>
                    </div>
                    <h6 className="mother-company-name">ASB Group of Companies</h6>
                  </div>
                  
                  <div className="brand-stats">
                    <div className="stat-item">
                      <FaStore className="stat-icon" />
                      <div className="stat-info">
                        <span className="stat-value">17+</span>
                        <span className="stat-label">Branches</span>
                      </div>
                    </div>
                    <div className="stat-item">
                      <FaRegHeart className="stat-icon" />
                      <div className="stat-info">
                        <span className="stat-value">50K+</span>
                        <span className="stat-label">Customers</span>
                      </div>
                    </div>
                  </div>
                  <div className="trust-indicators">
                    <div className="trust-item"><FaShieldAlt /> <span>100% Authentic</span></div>
                    <div className="trust-item"><FaTruck /> <span>Free Shipping</span></div>
                    <div className="trust-item"><FaUndo /> <span>Easy Returns</span></div>
                  </div>
                </div>
              </motion.div>

              {/* Quick Links Column */}
              <motion.div className="footer-col" variants={itemVariants}>
                <h4 className="footer-title">
                  <span className="title-text">Quick Links</span>
                  <span className="title-underline"></span>
                </h4>
                <ul className="footer-links">
                  {quickLinks.map((link, index) => (
                    <motion.li 
                      key={index}
                      onHoverStart={() => setActiveLink(index)}
                      onHoverEnd={() => setActiveLink(null)}
                    >
                      <Link to={link.path}>
                        <span className="link-name">{link.name}</span>
                        <motion.span 
                          className="link-indicator"
                          animate={{ width: activeLink === index ? '100%' : '0%' }}
                        />
                      </Link>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>

              {/* Contact Column */}
              <motion.div className="footer-col" variants={itemVariants}>
                <h4 className="footer-title">
                  <span className="title-text">Contact</span>
                  <span className="title-underline"></span>
                </h4>
                <div className="contact-list">
                  <div className="contact-row">
                    <FaRegBuilding className="contact-icon" />
                    <div className="contact-content">
                      <span className="contact-label">Head Office</span>
                      <p>266/2, Sri Rathanajothi Mawatha, Kuda Waskaduwa, Waskaduwa.</p>
                    </div>
                  </div>
                  <div className="contact-row">
                    <FaPhone className="contact-icon" />
                    <div className="contact-content">
                      <span className="contact-label">Hotline</span>
                      <p>071 905 7057</p>
                    </div>
                  </div>
                  <div className="contact-row">
                    <FaRegEnvelope className="contact-icon" />
                    <div className="contact-content">
                      <span className="contact-label">Email</span>
                      <p>info@asbfashion.com</p>
                    </div>
                  </div>
                  <div className="contact-row">
                    <FaRegClock className="contact-icon" />
                    <div className="contact-content">
                      <span className="contact-label">Hours</span>
                      <p>9:00 AM - 8:00 PM (Mon-Sun)</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Newsletter Column */}
              <motion.div className="footer-col" variants={itemVariants}>
                <h4 className="footer-title">
                  <span className="title-text">Newsletter</span>
                  <span className="title-underline"></span>
                </h4>
                <div className="newsletter-container">
                  <p className="newsletter-description">Subscribe to receive exclusive updates and offers</p>
                  
                  {/* Error Message Display */}
                  <AnimatePresence>
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="newsletter-error-message"
                      >
                        <FaExclamationTriangle className="error-icon" />
                        <span>{error}</span>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  
                  {/* Success Message Display */}
                  <AnimatePresence>
                    {success && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="newsletter-success-message"
                      >
                        <FaCheckCircle className="success-icon" />
                        <span>{success}</span>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  
                  {/* Subscription Form */}
                  <AnimatePresence mode="wait">
                    {!isSubscribed && !success ? (
                      <motion.form
                        key="form"
                        onSubmit={handleSubscribe}
                        className="newsletter-form"
                        initial={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        <div className="input-container">
                          <InputGroup>
                            <Form.Control
                              type="email"
                              placeholder="Your email address"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              required
                              disabled={isLoading}
                              className={`newsletter-input ${error ? 'error-input' : ''}`}
                            />
                            <Button 
                              type="submit" 
                              className="newsletter-submit"
                              disabled={isLoading}
                            >
                              {isLoading ? (
                                <span className="spinner-border spinner-border-sm" />
                              ) : (
                                <FaRegPaperPlane />
                              )}
                            </Button>
                          </InputGroup>
                        </div>
                        <p className="privacy-note">
                          <FaShieldAlt /> Your data is protected. We never share your information.
                        </p>
                      </motion.form>
                    ) : (
                      <motion.div
                        key="success"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="subscribe-confirmation"
                      >
                        <FaCheckCircle className="confirmation-icon" />
                        <div className="confirmation-text">
                          <strong>Welcome to ASB Family!</strong>
                          <span>You'll receive our latest updates soon</span>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="social-section">
                    <span className="social-label">Connect with us</span>
                    <div className="social-grid">
                      {socialMedia.map((social, index) => (
                        <motion.a
                          key={index}
                          href={social.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="social-link"
                          whileHover={{ y: -3 }}
                          whileTap={{ scale: 0.95 }}
                          title={social.name}
                        >
                          {social.icon}
                        </motion.a>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        <motion.div className="footer-bottom" variants={itemVariants}>
          <div className="container">
            <div className="bottom-bar">
              <div className="copyright-info">
                <p>
                  © {currentYear} <strong>ASB FASHION</strong>. All rights reserved.
                </p>
                <div className="footer-links-bottom">
                  <Link to="/privacy">Privacy</Link>
                  <Link to="/terms">Terms</Link>
                  
                </div>
              </div>
              
              {/* Company Info Section */}
              <div className="company-info-footer">
                <div className="mother-company-info">
                  <span className="mother-label">A Division of</span>
                  <strong className="mother-name">ASB Group of Companies</strong>
                </div>
              </div>
              
              <motion.div className="developer-credit" whileHover={{ scale: 1.02 }}>
                <div className="credit-main">
                  <span className="credit-prefix">Developed by</span>
                  <a href="https://vexelit.xyz" target="_blank" rel="noopener noreferrer" className="company-credit">VEXEL IT</a>
                  <span className="developer-name">by Kavizz</span>
                </div>
                <div className="developer-contact">
                  <a href="mailto:vexelit.sl@gmail.com" className="dev-email">
                    <FaEnvelope className="dev-email-icon" />
                    vexelit.sl@gmail.com
                  </a>
                </div>
                <div className="method-indicator">
                  <FaMobile /> Memoth Method
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </motion.footer>

     
     
    </>
  );
};

export default Footer;